// Представление «На что потрачено» (до 01.10.2026 «Расход»): куда ушли деньги в полях за месяц
// и сколько осталось от выданного.
//
// Вопрос, на который сегодня нет ответа вовсе: «на что именно ушёл миллион». Ведомость знает
// зарплату, подотчёт знает остатки, а куда потрачено — только в головах старших.
//
// Сверху «Сверка месяца»: выдали, потратили, осталось, как «Остаток» внизу листа «итог» в книге
// заказчика. Под ней разрез по статьям и полоса за тридцать дней.
//
// Считаются ПРИНЯТЫЕ заявки. Поданная — это ещё не расход, а намерение: показать её здесь
// значило бы назвать деньги потраченными до того, как финотдел их признал. К месяцу заявка
// относится по дню траты, а не по дате приёмки: у заявок из книг заказчика приёмки нет.
//
// Доли и «осталось» приходят с сервера. Делить и вычитать те же числа второй раз на экране — это
// второй ответ на один вопрос, и однажды они разойдутся с файлом Excel.
//
// Месяц по умолчанию: последний, где есть что показать (не позже текущего). Вкладка открывается
// без месяца в адресе, а майская книга лежит в мае: на текущем месяце человек видел «расходов
// нет» и листал назад пять раз. Сервер отвечает ближайшим непустым месяцем (`latest_if_empty`), а
// экран говорит об этом одной строкой над блоками. Месяц, выбранный человеком, главнее: он
// записан в адрес, и подсказка к нему не применяется.

import { RiArrowLeftRightLine, RiPieChartLine } from '@remixicon/react';

import type { ApiError } from '@/shared/errors/api-error';
import type { SearchState } from '@/shared/routing/search';
import { countLabel } from '@/shared/text/plural';
import { formatDataAsOf, parseMoment } from '@/shared/time/format';
import {
  formatPeriodKey,
  formatPeriodTitle,
  месяцИзАдреса,
  parsePeriod,
  shiftPeriod,
} from '@/shared/time/period';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import { exportCashFlow } from '@/shared/api/endpoints/reports';
import { ExportButton } from '@/shared/reports/ExportButton';
import { PrintButton } from '@/shared/reports/PrintButton';
import { PeriodStepper } from '@/shared/ui/period-stepper';
import { Block } from '@/shared/ui/block';
import { ДеньгиВПолях } from '@/shared/reports/ДеньгиВПолях';
import { дельтаКПрошлому } from '@/shared/reports/дельта';
import { СтруктураСуммы } from '@/shared/reports/СтруктураСуммы';
import { useLegalEntitiesQuery } from '@/shared/org/queries';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { Toolbar } from '@/shared/ui/toolbar';
import { cn } from '@/utils/cn';

import {
  ПоказанПоследнийМесяц,
  ЧТО_ВЫДАЧИ_И_РАСХОДЫ,
  ЧТО_РАСХОДЫ,
} from '../components/ПоказанПоследнийМесяц';
import { ПустойМесяц } from '../components/ПустойМесяц';
import { СверкаМесяца, естьСверка } from '../components/СверкаМесяца';
import { подписьОтбора } from '../components/подпись-отбора';
import { useCashFlow, useCashTrend, usePersons } from '../queries';
import { FILTER_ENTITY, FILTER_PERIOD, FILTER_PERSON } from '../route';

/** «2026-08-01» → период. Данные несут дату начала периода, а не его название. */
function parsePeriodFromIso(iso: string) {
  const [год, месяц] = iso.split('-');
  return { year: Number(год), month: Number(месяц) };
}

export function CashFlow({
  search,
  onFilter,
  onClearFilters,
}: {
  search: SearchState;
  onFilter: (key: string, value: string | null) => void;
  /** Снять несколько отборов разом: по одному они затирали бы друг друга в адресе. */
  onClearFilters: (keys: readonly string[]) => void;
}) {
  // Месяц, выбранный человеком (стрелкой, ссылкой, кнопкой), записан в адресе и главнее любой
  // подсказки. Без него запрашивается текущий, а флаг просит сервер: если в нём пусто, ответить
  // ближайшим более ранним месяцем, где что-то было.
  const заданный = месяцИзАдреса(search.filters[FILTER_PERIOD]);
  const сегодня = parsePeriod(undefined);
  const запрошенный = заданный ?? сегодня;
  // Тз, раздел 5.7: «разрез по юридическому лицу и руководителю». На экране подпись «Человек»:
  // подотчётным бывает не только руководитель. Сервер их принимал с самого
  // начала, а спросить было негде: экран показывал расход по всем и всегда.
  const юрлицо = search.filters[FILTER_ENTITY];
  const лицо = search.filters[FILTER_PERSON];
  const отчёт = useCashFlow({
    year: запрошенный.year,
    month: запрошенный.month,
    legal_entity_id: юрлицо,
    accountable_person_id: лицо,
    latest_if_empty: заданный === null ? true : undefined,
  });
  // Месяц, данные которого на экране, берётся из ответа, а не из адреса: без месяца в адресе
  // сервер мог ответить и другим. Пока ответа нет, а месяца в адресе тоже нет, какой из них
  // покажут, неизвестно, и календарь стоит с заглушкой (текущий месяц, подставленный «пока едет»,
  // на ответе сменился бы на другой). Ошибка заглушку не держит: ответа уже не будет.
  const месяцДанных = отчёт.data === undefined ? null : parsePeriodFromIso(отчёт.data.period_from);
  // Пусто только тогда, когда за месяц не было ни расхода, ни выдач, ни возвратов. Месяц, где
  // деньги выдали, а потратить не успели, — не пустой: сверка («выдали 12 000, потрачено 0») как
  // раз про него.
  const отчётПуст = (данные: NonNullable<typeof отчёт.data>) =>
    данные.items.length === 0 &&
    (данные.issued_total ?? 0) === 0 &&
    (данные.returned_total ?? 0) === 0;
  // Пока едет другой месяц, на экране остаётся прежний срез (правило 11), но приглушённый: его не
  // принять за срез выбранного месяца. ПУСТОЙ прежний срез не остаётся вовсе: «За этот месяц
  // принятых расходов нет» под календарём уже другого месяца было бы неправдой (нажали «Открыть
  // май», а экран секунду уверял, что в мае расходов нет).
  const устарело = отчёт.isPlaceholderData;
  const данныеЭкрана =
    устарело && отчёт.data !== undefined && отчётПуст(отчёт.data) ? undefined : отчёт.data;
  // Выбранный человеком месяц пуст: где ближайшие данные? Тот же флаг, что открывает вкладку на
  // последнем месяце с данными, но запрашивается только для пустого месяца без отборов, чтобы
  // главная кнопка пустого экрана вела прямо к ним, а не к соседнему пустому месяцу. Прежний срез
  // не в счёт: по нему второй запрос уходил бы при каждом шаге на непустой месяц.
  const пустойВыбранный =
    заданный !== null &&
    отчёт.data !== undefined &&
    !устарело &&
    отчётПуст(отчёт.data) &&
    юрлицо === undefined &&
    лицо === undefined;
  const ближайшийОтчёт = useCashFlow(
    {
      year: запрошенный.year,
      month: запрошенный.month,
      legal_entity_id: юрлицо,
      accountable_person_id: лицо,
      latest_if_empty: true,
    },
    пустойВыбранный,
  );
  const ближайший =
    пустойВыбранный && ближайшийОтчёт.data !== undefined
      ? parsePeriodFromIso(ближайшийОтчёт.data.period_from)
      : null;
  const ближайшийС =
    ближайший !== null &&
    (ближайший.year !== запрошенный.year || ближайший.month !== запрошенный.month)
      ? ближайший
      : null;
  const месяцНеизвестен = заданный === null && отчёт.data === undefined && !отчёт.isError;
  const период = заданный ?? месяцДанных ?? сегодня;
  // Прошлый месяц — отдельным запросом и ТЕМ ЖЕ отчётом. Считать «сколько было» из другого
  // источника значило бы сравнивать разные величины и однажды показать рост там, где его нет.
  // «Прошлый» считается от месяца на экране, а не от текущего; пока он неизвестен, запроса нет.
  // Флага у этого запроса нет никогда: «сколько было в апреле» не должно подменяться другим.
  const прошлый = parsePeriod(formatPeriodKey(shiftPeriod(период, -1)));
  const отчётПрошлого = useCashFlow(
    {
      year: прошлый.year,
      month: прошлый.month,
      legal_entity_id: юрлицо,
      accountable_person_id: лицо,
    },
    !месяцНеизвестен,
  );
  // Полоса за тридцать дней календарём НЕ управляется: она всегда про «сейчас». Отбор по
  // юрлицу и руководителю на неё действует — это тот же вопрос, только в другом разрезе.
  const полоса = useCashTrend({
    days: 30,
    legal_entity_id: юрлицо,
    accountable_person_id: лицо,
  });
  const юрлица = useLegalEntitiesQuery();
  // Все подотчётные лица, а не только действующие: расход по человеку, которому подотчётность уже
  // сняли, принят и числится в отчёте, и разрез по нему нужно уметь выбрать.
  const лица = usePersons({ active_only: false });

  const сдвинуть = (на: number) =>
    onFilter(FILTER_PERIOD, formatPeriodKey(shiftPeriod(период, на)));
  const естьОтбор = юрлицо !== undefined || лицо !== undefined;
  // Отбор словами: шапка на печать не идёт, и итог без этой подписи читался бы как итог месяца
  // целиком, хотя отобрано одно юрлицо или один человек.
  const выбранноеЮрлицо = юрлица.data?.items.find((строка) => строка.id === юрлицо);
  const отбор = подписьОтбора([
    выбранноеЮрлицо?.short_name ?? выбранноеЮрлицо?.name,
    лица.data?.items.find((строка) => строка.id === лицо)?.employee_name,
  ]);

  return (
    <main className='flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-bg-weak-50'>
      <Toolbar
        actions={
          <ExportButton
            собрать={() =>
              exportCashFlow({
                year: период.year,
                month: период.month,
                legal_entity_id: юрлицо,
                accountable_person_id: лицо,
              })
            }
          />
        }
      >
        {/* Раньше здесь листали месяц словами «Раньше» и «Позже», а в соседнем разделе —
            стрелками. Календарь в продукте один. */}
        <PeriodStepper
          label='Месяц расхода'
          title={месяцНеизвестен ? null : formatPeriodTitle(период)}
          onPrev={() => сдвинуть(-1)}
          onNext={() => сдвинуть(1)}
        />
        <div className='min-w-56'>
          <SelectField
            id='cashflow-entity'
            label='Юрлицо'
            value={юрлицо ?? NO_VALUE}
            options={(юрлица.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.short_name ?? строка.name,
            }))}
            noneLabel='все юрлица'
            placeholder='все юрлица'
            pending={юрлица.isPending}
            failed={юрлица.isError}
            failedError={юрлица.error as ApiError | null}
            onChange={(значение) =>
              onFilter(FILTER_ENTITY, значение === NO_VALUE ? null : значение)
            }
          />
        </div>
        <div className='min-w-56'>
          <SelectField
            id='cashflow-person'
            label='Человек'
            value={лицо ?? NO_VALUE}
            options={(лица.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.employee_name,
            }))}
            noneLabel='все люди'
            placeholder='все люди'
            pending={лица.isPending}
            failed={лица.isError}
            failedError={лица.error as ApiError | null}
            onChange={(значение) =>
              onFilter(FILTER_PERSON, значение === NO_VALUE ? null : значение)
            }
          />
        </div>
        {/* Печать обещана тз в одном ряду с выгрузкой в Excel (раздел 5.7), и до 06.09.2026
            её не было ни у одного отчёта, кроме ведомости. Отчёты подписывают и подшивают, а
            Excel для этого надо сначала открыть на машине, где он есть. */}
        <PrintButton />
      </Toolbar>

      {/* Ширина чтения ограничена.
          
          На мониторе в 1680 точек строка категории растягивалась на 1400: «Хозяйственные
          нужды» слева, «1 заявка · 3 200,00 ₽» у правого края, между ними тысяча точек
          пустоты. Глаз проходит это расстояние дольше, чем читает обе половины, а при двух
          категориях сравнить суммы становится нечем — они на разных концах экрана.
          
          Потолок тот же, что у сводки реестра и состава объектов: `max-w-metrics`. */}
      <div data-печать='документ' className='flex-1 overflow-y-auto p-5'>
        <div className='flex max-w-metrics flex-col gap-5'>
          <ScreenState
            data={данныеЭкрана}
            error={отчёт.error as ApiError | null}
            subject='движение денег в полях'
            isEmpty={отчётПуст}
            onRetry={() => void отчёт.refetch()}
            // Заглушка повторяет форму будущего: сверка сверху и блок со статьями под ней.
            loading={
              <div className='flex flex-col gap-5'>
                <Skeleton className='h-28 w-full' />
                <Skeleton className='h-56 w-full' />
              </div>
            }
            empty={
              <ПустойМесяц
                месяц={период}
                естьОтбор={естьОтбор}
                снять={() => onClearFilters([FILTER_ENTITY, FILTER_PERSON])}
                назад={() => сдвинуть(-1)}
                ближайший={ближайшийС}
                кБлижайшему={() =>
                  ближайшийС !== null && onFilter(FILTER_PERIOD, formatPeriodKey(ближайшийС))
                }
                // Месяц записывается явно: «К текущему месяцу» обещает именно его, а без месяца в
                // адресе вкладка ушла бы искать последний, где что-то было.
                кТекущему={() => onFilter(FILTER_PERIOD, formatPeriodKey(сегодня))}
                значок={RiPieChartLine}
                картинка='расход'
                заголовокПусто={() => 'За этот месяц принятых расходов нет'}
                описаниеПусто='Здесь появятся суммы по категориям, как только финотдел примет первые чеки. Поданные, но ещё не принятые заявки сюда не попадают: это намерение, а не трата.'
                заголовокОтбор='Под этот отбор расходов нет'
                описаниеОтбор='За этот месяц по выбранному юрлицу или человеку принятых расходов не нашлось. Снимите отбор, чтобы посмотреть расход по всем.'
              />
            }
          >
            {(данные) => {
              const срез = parseMoment(данные.generated_at);
              const срезСловами = [срез === null ? undefined : formatDataAsOf(срез), отбор]
                .filter((часть) => часть !== undefined)
                .join(' · ');
              // Срез данных подписан один раз на экран: у сверки, а если сверки нет (старший
              // объекта, отбор по человеку, старый сервер) — у блока со статьями, как было всегда.
              const сверкаЕсть = естьСверка(данные);
              const было = отчётПрошлого.data?.total_amount;
              const дельта = дельтаКПрошлому(данные.total_amount, было);
              // Месяц берётся из САМИХ данных, а не из адреса. Пока едет новый месяц, на экране
              // остаётся прошлый срез (правило 11), и подпись из адреса стояла бы рядом с суммой
              // другого месяца — то есть врала.
              const месяц = formatPeriodTitle(parsePeriodFromIso(данные.period_from)).toLowerCase();
              return (
                <div
                  aria-busy={устарело}
                  className={cn('flex flex-col gap-5 transition-opacity', устарело && 'opacity-60')}
                >
                  {заданный === null && (
                    <ПоказанПоследнийМесяц
                      показан={месяцДанных ?? запрошенный}
                      запрошен={сегодня}
                      что={сверкаЕсть ? ЧТО_ВЫДАЧИ_И_РАСХОДЫ : ЧТО_РАСХОДЫ}
                    />
                  )}
                  <СверкаМесяца
                    данные={данные}
                    месяц={месяц}
                    юрлицоВыбрано={юрлицо !== undefined}
                    срез={срезСловами}
                  />
                  {данные.items.length === 0 ? (
                    // Деньги выдавали, а принятых расходов за месяц нет: сверка выше говорит о
                    // выданном, а здесь сказано, почему разреза по статьям нет.
                    <Block icon={RiPieChartLine} title='Сколько потратили'>
                      <EmptyState
                        icon={RiPieChartLine}
                        title='За этот месяц принятых расходов нет'
                        description='Поданные, но ещё не принятые заявки сюда не попадают: это намерение, а не трата.'
                      />
                    </Block>
                  ) : (
                    <Block
                      icon={RiPieChartLine}
                      title='Сколько потратили'
                      note={сверкаЕсть ? undefined : срезСловами}
                    >
                      <СтруктураСуммы
                        label={`Потрачено за ${месяц}`}
                        total={данные.total_amount}
                        // Итог крупным числом выключается там, где то же число уже стоит в ряду
                        // величин над блоком: две одинаковые суммы подряд человек читает как две
                        // разные величины и ищет между ними разницу (`СтруктураСуммы`). Сверка
                        // месяца выше уже называет «− Потрачено» и число принятых заявок, поэтому
                        // здесь остаётся только то, чего в ней нет: как это к прошлому месяцу.
                        showTotal={!сверкаЕсть}
                        footnote={
                          сверкаЕсть
                            ? дельта?.text
                            : countLabel(данные.total_claims, [
                                'принятая заявка',
                                'принятые заявки',
                                'принятых заявок',
                              ])
                        }
                        badge={сверкаЕсть ? undefined : дельта}
                        nameHeader='Статья'
                        noteHeader='Заявок'
                        rows={данные.items.map((строка) => ({
                          id: строка.category_id,
                          title: строка.category_title,
                          amount: строка.amount,
                          percent: строка.percent,
                          note: countLabel(строка.claims, ['заявка', 'заявки', 'заявок']),
                        }))}
                      />
                    </Block>
                  )}
                </div>
              );
            }}
          </ScreenState>

          {/* Вторая карточка отвечает на третий вопрос экрана, которого у него не было: расход
            за месяц говорит «сколько потратили», разрез по статьям — «на что», и ни один из
            них не говорит, РАСТЁТ ли подотчёт. Полоса календарём не управляется: она всегда
            про последние тридцать дней, и подписана этим прямо. */}
          <ScreenState
            data={полоса.data}
            error={полоса.error as ApiError | null}
            subject='движение денег в полях по дням'
            isEmpty={(данные) =>
              данные.total_issued === 0 && данные.total_returned === 0 && данные.total_spent === 0
            }
            onRetry={() => void полоса.refetch()}
            loading={<Skeleton className='h-40 w-full' />}
            empty={
              <EmptyState
                icon={RiArrowLeftRightLine}
                title='За последние тридцать дней денег в полях не двигали'
                illustration='деньги'
                description='Здесь появится линия: выдали, вернули, приняли расходом. По ней видно, растёт подотчёт или тает.'
              />
            }
          >
            {(данные) => (
              <Block icon={RiArrowLeftRightLine} title='Деньги в полях за тридцать дней'>
                <ДеньгиВПолях data={данные} />
              </Block>
            )}
          </ScreenState>
        </div>
      </div>
    </main>
  );
}
